import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

import { useAuth } from "context/AuthContext";
import useAuthCheck from "config/useAuthCheck";

// @mui material components
import Pagination from "@mui/material/Pagination";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React contexts
import { useMaterialUIController } from "context";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDInput from "components/MDInput";
import MDTypography from "components/MDTypography";
// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

import { getSuppliers, deleteSupplier } from "../../../../api/suppliers";

import DeleteModal from "../../../../customComponents/devicesComponents/DeleteModal";

import CreateEditSupplier from "../../../../customComponents/adminComponents/CreateEditSupplier";

import { setAllSuppliers } from "../../../../redux/reducers/suppliersReducer";
import { useDispatch, useSelector } from "react-redux";

import { deviceType } from "config/enum";

import {
  setSupplierDeviceType,
  setSupplierValueBigFilter,
  setSupplierSelectBigFilter,
} from "../../../../redux/reducers/filtersReducer";
import { deviceTypeEnum, deviceTypes } from "config/enum";

export default function Suppliers() {
  useAuthCheck();
  const { user } = useAuth();

  const [suppliers, setSuppliers] = useState(null);
  const [page, setPage] = useState(1);

  const navigate = useNavigate();

  const supplierDeviceType = useSelector((state) => state.filters.supplierDeviceType);
  const supplierValueBigFilter = useSelector((state) => state.filters.supplierValueBigFilter);
  const supplierSelectBigFilter = useSelector((state) => state.filters.supplierSelectBigFilter);

  const handlePageChange = (event, value) => {
    setPage(value);
  };

  const bigFilterInfo = {
    Nom: "tblsupplier.supplier_name",
  };

  const [query, setQuery] = useState({});

  const updateQueryBasedOnFilters = () => {
    let updatedQuery = {};
    if (supplierDeviceType) updatedQuery[`tblsupplier.${deviceType[supplierDeviceType]}`] = 1;
    if (supplierSelectBigFilter) {
      updatedQuery[bigFilterInfo[supplierSelectBigFilter]] =
        encodeURIComponent(supplierValueBigFilter);
    }
    return updatedQuery;
  };

  const fetchSuppliersWithFilters = async (additionalQuery = {}) => {
    if (user) {
      const filtersQuery = updateQueryBasedOnFilters();
      const response = await getSuppliers({ ...filtersQuery, ...query, ...additionalQuery });
      setSuppliers(response.data);
    }
  };

  const fetchSuppliers = async () => {
    const response = await getSuppliers();
    const formatedSuppliers = await response.data.map((supplier) => ({
      label: supplier.supplier_name,
      id: supplier.supplier_id,
    }));
    dispatch(setAllSuppliers(formatedSuppliers));
  };

  function updateQuery(key, value) {
    if (value === null)
      setQuery((query) => {
        const copy = { ...query };
        delete copy[key];
        return copy;
      });
    else setQuery((query) => ({ ...query, [key]: value }));
  }

  const dispatch = useDispatch();

  const [controller] = useMaterialUIController();
  const { darkMode, cardBoard } = controller;

  useEffect(() => {
    if (user && user.role !== "Admin" && user.role !== "Pilote") {
      navigate("/accueil");
    } else {
      fetchSuppliersWithFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supplierDeviceType, supplierSelectBigFilter, supplierValueBigFilter, user]);

  async function deleteSupplierAPI(user) {
    await deleteSupplier(user);
  }

  function paginate(array, page_size, page_number) {
    // human-readable page numbers usually start with 1, so we reduce 1 in the first argument
    return array.slice((page_number - 1) * page_size, page_number * page_size);
  }

  async function submit(user) {
    await deleteSupplierAPI(user);
    await fetchSuppliersWithFilters();
    await fetchSuppliers();
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={3} pb={1.5}>
        <Grid container justifyContent="center" direction="row" spacing={2}>
          <Grid
            item
            lg={2.8}
            md={4}
            sm={5}
            xs={10}
            sx={{
              border: "1px solid lightGrey",
              borderRadius: "8px",
              marginTop: 2,
              marginLeft: 2,
              padding: "1rem !important",
            }}
          >
            <MDTypography variant="subtitle1" align="center">
              Filtre de précision
            </MDTypography>
            <Tooltip
              title={supplierSelectBigFilter ? "" : "Veuillez sélectionner un élément à cibler"}
              placement="top"
            >
              <div>
                <TextField
                  fullWidth
                  variant="outlined"
                  value={supplierValueBigFilter}
                  onChange={(event) => {
                    dispatch(setSupplierValueBigFilter(event.target.value));
                    updateQuery(bigFilterInfo[supplierSelectBigFilter], event.target.value);
                    setPage(1);
                  }}
                  inputProps={{
                    style: {
                      height: 29.13,
                    },
                    maxLength: 255,
                  }}
                  type="text"
                  label="Valeur à rechercher"
                  disabled={!supplierSelectBigFilter}
                  sx={{ marginBottom: 2, marginTop: 1 }}
                />
              </div>
            </Tooltip>
            <Autocomplete
              options={Object.keys(bigFilterInfo)}
              value={supplierSelectBigFilter}
              onChange={(event, newBigFilter) => {
                if (newBigFilter) {
                  // If a new filter is selected, update the query and keep the current input value
                  dispatch(setSupplierSelectBigFilter(newBigFilter));
                } else {
                  // If the filter is cleared, remove the corresponding query part and clear the input
                  setQuery((currentQuery) => {
                    const newQuery = { ...currentQuery };
                    Object.values(bigFilterInfo).forEach((filterKey) => {
                      if (filterKey in newQuery) {
                        delete newQuery[filterKey];
                      }
                    });
                    return newQuery;
                  });
                  dispatch(setSupplierSelectBigFilter(null));
                  dispatch(setSupplierValueBigFilter(""));
                }
                setPage(1);
              }}
              renderInput={(params) => (
                <MDInput pb={0} {...params} label="Élément à cibler" variant="outlined" />
              )}
            />
          </Grid>
          <Grid
            item
            container
            direction="column"
            justifyContent="center"
            spacing={2}
            lg={2.4}
            md={4}
            sm={5}
            xs={10}
            sx={{
              padding: "1rem !important",
              border: "1px solid lightGrey",
              borderRadius: "8px",
              textAlign: "center",
              marginTop: 2,
              marginLeft: 2,
            }}
          >
            <MDTypography mb={1} variant="subtitle1" align="center">
              Filtres normaux
            </MDTypography>
            <Grid direction="row" spacing={2} container justifyContent="center">
              <Grid item xs={12}>
                <Autocomplete
                  options={deviceTypeEnum}
                  value={supplierDeviceType}
                  onChange={(event, newDeviceType) => {
                    setQuery((currentQuery) => {
                      const newQuery = { ...currentQuery };
                      Object.values(deviceTypes).forEach((filterKey) => {
                        if (filterKey in newQuery) {
                          delete newQuery[filterKey];
                        }
                      });
                      return newQuery;
                    });
                    if (newDeviceType !== null) {
                      dispatch(setSupplierDeviceType(newDeviceType));
                      updateQuery(`tblsupplier.${deviceType[newDeviceType]}`, 1);
                    } else {
                      dispatch(setSupplierDeviceType(null));
                    }
                    setPage(1);
                  }}
                  renderInput={(params) => (
                    <TextField pb={0} {...params} label="Types d'appareils" variant="outlined" />
                  )}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid
            direction="row"
            item
            container
            justifyContent="center"
            lg={12}
            md={12}
            sm={12}
            xs={12}
          >
            <Grid item sx={{ padding: 1, marginTop: 3 }} lg={3} md={6} sm={6} xs={12}>
              <CreateEditSupplier
                suppliers={suppliers}
                fetchSuppliersWithFilters={fetchSuppliersWithFilters}
                edit={false}
              />
            </Grid>
          </Grid>
        </Grid>
      </MDBox>
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          {suppliers ? (
            !cardBoard ? (
              paginate(suppliers, 12, page).map((supplier) => (
                <Grid item mb={1.5} lg={3} md={6} xs={12}>
                  <Card sx={{ padding: "0 1rem" }}>
                    <MDBox
                      item
                      mx={0.5}
                      mt={-3}
                      py={3}
                      px={2}
                      sx={{
                        background: !darkMode
                          ? "linear-gradient(195deg, #364c70, #273956)"
                          : "white",
                        boxShadow: `0 1px 6px 1px ${!darkMode ? "#344767" : "white"}`,
                      }}
                      variant="gradient"
                      borderRadius="lg"
                    >
                      <MDTypography
                        variant="h5"
                        textAlign="center"
                        color={!darkMode ? "white" : "#344767"}
                      >
                        {supplier.supplier_name ? supplier.supplier_name : "Nom inconnu"}
                      </MDTypography>
                    </MDBox>
                    <MDBox textAlign="center" pb={3} pt={3}>
                      <Grid
                        container
                        rowSpacing={1.5}
                        columnSpacing={{ xs: 2 }}
                        justifyContent="center"
                      >
                        <Grid item>
                          <CreateEditSupplier
                            suppliers={suppliers}
                            fetchSuppliersWithFilters={fetchSuppliersWithFilters}
                            supplier={supplier}
                            edit
                          />
                        </Grid>
                        <Grid item>
                          <DeleteModal
                            message={`le fournisseur ${supplier.supplier_name}`}
                            onOk={() => {
                              submit(supplier.supplier_id);
                            }}
                            type="supplier"
                          />
                        </Grid>
                      </Grid>
                    </MDBox>
                  </Card>
                </Grid>
              ))
            ) : (
              paginate(suppliers, 12, page).map((supplier) => (
                <Grid
                  item
                  container
                  xs={12}
                  sx={{ paddingTop: "0px !important", marginBottom: "0px !important" }}
                >
                  <Grid item mt={0.5} mb={0.5} xs={12}>
                    <Divider
                      sx={{
                        margin: "0.5rem 0rem",
                        background: !darkMode ? "#000" : "#FFF",
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    container
                    sx={{ alignItems: "center", display: "flex", justifyContent: "center" }}
                  >
                    <Grid mt={0.75} item lg={2.5} xs={12}>
                      <MDTypography variant="h5" textAlign="center">
                        {supplier.supplier_name ? supplier.supplier_name : "Nom inconnu"}
                      </MDTypography>
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <CreateEditSupplier
                        suppliers={suppliers}
                        fetchSuppliersWithFilters={fetchSuppliersWithFilters}
                        supplier={supplier}
                        edit
                      />
                    </Grid>
                    <Grid item lg={2} xs={12} textAlign="center">
                      <DeleteModal
                        message={`le fournisseur ${supplier.supplier_name}`}
                        onOk={() => {
                          submit(supplier.supplier_id);
                        }}
                        type="supplier"
                      />
                    </Grid>
                  </Grid>
                </Grid>
              ))
            )
          ) : (
            <Grid item xs={12} justifyContent="center">
              <MDTypography pb={2} variant="h5" textAlign="center">
                Aucun fournisseur à afficher
              </MDTypography>
            </Grid>
          )}
          {suppliers &&
            (suppliers.length <= 12 ? (
              <></>
            ) : (
              <Grid item container xs={12} mt={3} justifyContent="center">
                <Stack spacing={2}>
                  <Pagination
                    page={page}
                    onChange={handlePageChange}
                    variant="text"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                      button: {
                        color: !darkMode ? "#1A2035" : "#FFF",
                      },
                      "button.Mui-selected": { color: !darkMode ? "#FFF" : "#1A2035" },
                    }}
                    count={Math.ceil(suppliers.length / 12)}
                    color={darkMode ? "white" : "dark"}
                  ></Pagination>
                </Stack>
              </Grid>
            ))}
        </Grid>
      </MDBox>
    </DashboardLayout>
  );
}
